import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  UserCircleIcon,
  ClockIcon,
  CreditCardIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  ArrowTopRightOnSquareIcon,
  PencilIcon,
  PlusIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  InformationCircleIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { useUserStore } from '../store/userStore';
import { userApi } from '../services/api';
import { MembershipPass } from '../types';
import PasswordChangeModal from '../domains/auth/components/PasswordChangeModal';

interface UserStats {
  total_sessions: number;
  total_study_minutes: number;
  average_session_minutes: number;
  total_days_visited: number;
  current_month_minutes: number;
}

const UserProfile: React.FC = () => {
  const {
    currentUser,
    currentSession,
    membershipPasses,
    setMembershipPasses,
    logout,
  } = useUserStore();
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: currentUser?.name || '',
    phone: currentUser?.phone || '',
  });
  const [loading, setLoading] = useState(true);
  const [currentStudyTime, setCurrentStudyTime] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [copied, setCopied] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);

  // 실시간 공부 시간 계산
  useEffect(() => {
    if (currentSession?.check_in_at) {
      const timer = setInterval(() => {
        const checkInTime = new Date(currentSession.check_in_at);
        const now = new Date();
        const diff = Math.floor((now.getTime() - checkInTime.getTime()) / 1000);

        const hours = Math.floor(diff / 3600);
        const minutes = Math.floor((diff % 3600) / 60);
        const seconds = diff % 60;

        setCurrentStudyTime({ hours, minutes, seconds });
      }, 1000);

      return () => clearInterval(timer);
    } else {
      setCurrentStudyTime({ hours: 0, minutes: 0, seconds: 0 });
    }
  }, [currentSession]);

  // 사용자 데이터 로드
  useEffect(() => {
    const loadUserData = async () => {
      if (!currentUser) return;

      setLoading(true);
      try {
        // 이용권 정보 로드
        const passes = await userApi.getMembershipPasses(currentUser.id.toString());
        setMembershipPasses(passes);

        // 통계 정보 로드
        const stats = await userApi.getStats(currentUser.id.toString());
        console.log(
          `📈 총 ${stats.total_sessions}회 이용, 총 ${stats.total_study_minutes}분 공부`
        );
        setUserStats(stats);
      } catch (error) {
        console.error('Failed to load user data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [currentUser, setMembershipPasses]);

  // 내정보 탭 활성화 시 데이터 새로고침
  useEffect(() => {
    const refreshData = async () => {
      if (!currentUser || loading) return;

      try {
        // 통계 정보만 다시 로드 (빠른 업데이트)
        const stats = await userApi.getStats(currentUser.id.toString());
        setUserStats(stats);

        // 이용권 정보도 새로고침 (퇴실 후 차감 반영)
        const passes = await userApi.getMembershipPasses(currentUser.id.toString());
        setMembershipPasses(passes);
      } catch (error) {
        console.error('Failed to refresh data:', error);
      }
    };

    // 컴포넌트가 마운트될 때마다 새로고침
    const timeoutId = setTimeout(refreshData, 100);
    return () => clearTimeout(timeoutId);
  }, [currentUser, loading, setMembershipPasses]);

  const handleEditProfile = async () => {
    if (!currentUser) return;

    try {
      await userApi.update(currentUser.id, {
        name: editForm.name,
        phone: editForm.phone,
      });

      alert('프로필이 성공적으로 업데이트되었습니다.');
      setIsEditing(false);
      // 여기서 사용자 정보를 다시 로드하거나 상태를 업데이트해야 합니다
    } catch (error) {
      console.error('Failed to update profile:', error);
      alert('프로필 업데이트 중 오류가 발생했습니다.');
    }
  };

  // 사용자 ID 복사 함수
  const handleCopyUserId = async () => {
    if (currentUser?.user_number) {
      try {
        await navigator.clipboard.writeText(currentUser.user_number);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        console.error('클립보드 복사 실패:', error);
      }
    }
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}시간 ${mins}분`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getPassStatus = (pass: MembershipPass) => {
    const now = new Date();
    const expiresAt = new Date(pass.expires_at);

    if (!pass.is_active) return 'inactive';
    if (expiresAt < now) return 'expired';
    if (pass.remaining_minutes <= 0) return 'depleted';

    const daysUntilExpiry = Math.floor(
      (expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (daysUntilExpiry <= 7) return 'expiring';

    return 'active';
  };

  const getPassStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-700 bg-green-50 border-green-200';
      case 'expiring':
        return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      case 'expired':
        return 'text-red-700 bg-red-50 border-red-200';
      case 'depleted':
        return 'text-gray-700 bg-gray-50 border-gray-200';
      case 'inactive':
        return 'text-gray-700 bg-gray-50 border-gray-200';
      default:
        return 'text-gray-700 bg-gray-50 border-gray-200';
    }
  };

  const getPassStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return '이용중';
      case 'expiring':
        return '곧 만료';
      case 'expired':
        return '만료됨';
      case 'depleted':
        return '시간 소진';
      case 'inactive':
        return '비활성';
      default:
        return '알 수 없음';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 p-4 lg:p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-48 bg-gray-200 rounded"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="space-y-6 p-4 lg:p-6">
        <div className="text-center py-12">
          <UserCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            로그인이 필요합니다
          </h3>
          <p className="text-gray-500 mb-4">
            프로필을 보려면 먼저 로그인해주세요.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 lg:p-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">내 정보</h1>
        <button
          onClick={logout}
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          로그아웃
        </button>
      </div>

      {/* 사용자 ID 정보 */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900 flex items-center">
            <InformationCircleIcon className="h-5 w-5 mr-2 text-blue-500" />
            사용자 정보
          </h2>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-blue-900 mb-1">
                사용자 ID
              </h3>
              <p className="text-2xl font-mono font-bold text-blue-700">
                {currentUser.user_number || '10012501001'}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                개인 식별번호입니다. 문의 시 필요합니다.
              </p>
            </div>
            <button
              onClick={handleCopyUserId}
              className="inline-flex items-center px-3 py-2 border border-blue-300 shadow-sm text-sm leading-4 font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {copied ? (
                <>
                  <CheckIcon className="h-4 w-4 mr-2" />
                  복사됨
                </>
              ) : (
                <>
                  <ClipboardDocumentIcon className="h-4 w-4 mr-2" />
                  복사
                </>
              )}
            </button>
          </div>
        </div>

        {/* 독서실 정보 추가 */}
        {currentUser.studyroom_name && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center">
              <h3 className="text-sm font-medium text-gray-700 mr-2">
                소속 독서실:
              </h3>
              <p className="text-base font-medium text-gray-900">
                {currentUser.studyroom_name}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* 사용자 기본 정보 */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              <UserCircleIcon className="h-16 w-16 text-gray-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {currentUser.name}
              </h2>
              <p className="text-gray-500">
                전화번호: {currentUser.phone || '등록되지 않음'}
              </p>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="p-2 text-gray-400 hover:text-gray-600"
              title="프로필 수정"
            >
              <PencilIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => setShowPasswordChange(true)}
              className="p-2 text-gray-400 hover:text-gray-600"
              title="비밀번호 변경"
            >
              <LockClosedIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {isEditing && (
          <div className="border-t pt-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                이름
              </label>
              <input
                type="text"
                value={editForm.name}
                onChange={(e) =>
                  setEditForm({ ...editForm, name: e.target.value })
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                전화번호
              </label>
              <input
                type="tel"
                value={editForm.phone}
                onChange={(e) =>
                  setEditForm({ ...editForm, phone: e.target.value })
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleEditProfile}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                저장
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                취소
              </button>
            </div>
          </div>
        )}

        {currentSession && (
          <div className="mt-6 p-4 bg-primary-50 rounded-lg border border-primary-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <ClockIcon className="h-5 w-5 text-primary-600 mr-2" />
                <span className="text-sm font-medium text-primary-900">
                  현재 {currentSession.seat_id}번 좌석에서 이용중
                </span>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-primary-900">
                  {String(currentStudyTime.hours).padStart(2, '0')}:
                  {String(currentStudyTime.minutes).padStart(2, '0')}:
                  {String(currentStudyTime.seconds).padStart(2, '0')}
                </div>
                <div className="text-xs text-primary-700">순공부시간</div>
              </div>
            </div>
            <p className="text-xs text-primary-700 mt-2">
              입실시간:{' '}
              {new Date(currentSession.check_in_at).toLocaleString('ko-KR')}
            </p>
          </div>
        )}
      </div>

      {/* 이용권 정보 */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <CreditCardIcon className="h-5 w-5 mr-2" />
            이용권 현황
          </h3>
          <Link
            to="/payment"
            className="btn btn-primary text-sm flex items-center"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            이용권 구매
          </Link>
        </div>

        {membershipPasses.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <CreditCardIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">등록된 이용권이 없습니다</p>
          </div>
        ) : (
          <div className="space-y-4">
            {membershipPasses.map((pass) => {
              const status = getPassStatus(pass);
              return (
                <div
                  key={pass.id}
                  className={`p-4 border rounded-lg ${getPassStatusColor(status)}`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium">{pass.pass_type}</h4>
                      <p className="text-sm opacity-75">
                        구매일: {formatDate(pass.purchase_date)}
                      </p>
                      <p className="text-sm opacity-75">
                        만료일: {formatDate(pass.expires_at)}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium">
                        {getPassStatusText(status)}
                      </div>
                      <div className="text-lg font-bold">
                        {formatTime(pass.remaining_minutes)}
                      </div>
                      <div className="text-xs opacity-75">남은 시간</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 이용 통계 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <ChartBarIcon className="h-5 w-5 mr-2" />
          이용 통계
        </h3>

        {userStats ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-xl font-bold text-blue-600">
                {userStats.total_sessions}
              </div>
              <div className="text-sm text-blue-800">총 이용 횟수</div>
            </div>

            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-xl font-bold text-green-600">
                {Math.floor(userStats.total_study_minutes / 60)}
              </div>
              <div className="text-sm text-green-800">총 공부시간(시간)</div>
            </div>

            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-xl font-bold text-purple-600">
                {Math.floor(userStats.average_session_minutes)}
              </div>
              <div className="text-sm text-purple-800">평균 이용시간(분)</div>
            </div>

            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <div className="text-xl font-bold text-orange-600">
                {userStats.total_days_visited}
              </div>
              <div className="text-sm text-orange-800">총 방문일수</div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <ChartBarIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">통계 정보를 불러오는 중...</p>
          </div>
        )}
      </div>

      {/* 이번 달 이용 현황 */}
      {userStats && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <CalendarDaysIcon className="h-5 w-5 mr-2" />
            이번 달 이용 현황
          </h3>

          <div className="text-center p-6 bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg">
            <div className="text-3xl font-bold text-primary-600 mb-2">
              {formatTime(userStats.current_month_minutes)}
            </div>
            <div className="text-sm text-gray-600">이번 달 총 공부시간</div>

            {userStats.current_month_minutes > 0 && (
              <div className="mt-4 text-xs text-gray-500">
                하루 평균:{' '}
                {formatTime(
                  Math.floor(
                    userStats.current_month_minutes / new Date().getDate()
                  )
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 추가 기능 (향후 구현) */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">추가 서비스</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button className="flex items-center justify-between p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <div className="flex items-center">
              <div className="text-sm font-medium text-gray-900">
                이용 내역 다운로드
              </div>
            </div>
            <ArrowTopRightOnSquareIcon className="h-4 w-4 text-gray-400" />
          </button>

          <button className="flex items-center justify-between p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <div className="flex items-center">
              <div className="text-sm font-medium text-gray-900">알림 설정</div>
            </div>
            <ArrowTopRightOnSquareIcon className="h-4 w-4 text-gray-400" />
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-3 text-center">
          곧 추가될 기능입니다
        </p>
      </div>

      {/* 비밀번호 변경 모달 */}
      <PasswordChangeModal
        isOpen={showPasswordChange}
        onSuccess={() => {
          setShowPasswordChange(false);
          alert('비밀번호가 성공적으로 변경되었습니다.');
        }}
        onClose={() => setShowPasswordChange(false)}
      />
    </div>
  );
};

export default UserProfile;
